import React, { useEffect, useState, useCallback } from "react";
import { Download, RefreshCw, Trash2, Plus, Loader2, AlertCircle, Link2, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";

function hostOf(url) {
  try { return new URL(url.replace(/^webcal:\/\//i, "https://")).host; } catch { return url; }
}

function fmtSynced(iso) {
  if (!iso) return "Not synced yet";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `Synced ${d.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`;
}

export default function CalendarImport({ onChange, open: controlledOpen, onOpenChange, hideTrigger = false }) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange || setUncontrolledOpen;
  const [imports, setImports] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [del, setDel] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/calendar-imports"); setImports(data.items || []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const connect = async () => {
    if (!url.trim()) { toast.error("Paste a calendar link first"); return; }
    setConnecting(true);
    try {
      const { data } = await api.post("/coach/calendar-imports", { url: url.trim(), name: name.trim() });
      toast.success(`Connected — ${data.result.total} event(s) imported`);
      setName(""); setUrl("");
      await load(); onChange && onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setConnecting(false); }
  };

  const syncNow = async (imp) => {
    setBusyId(imp.id);
    try {
      const { data } = await api.post(`/coach/calendar-imports/${imp.id}/sync`);
      const r = data.result;
      toast.success(r.new ? `${r.new} new event(s) added` : "Up to date");
      await load(); onChange && onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const doDisconnect = async () => {
    const imp = del; setDel(null);
    try {
      const { data } = await api.delete(`/coach/calendar-imports/${imp.id}`);
      toast.success(`Disconnected — ${data.removed} event(s) removed`);
      await load(); onChange && onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      {!hideTrigger && (
        <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="calendar-import-open-btn">
          <Download className="mr-2 h-4 w-4" /> Import calendar
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="calendar-import-dialog" className="max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2 pr-6">
              <DialogTitle className="font-heading">Import a calendar</DialogTitle>
              <Button variant="ghost" size="sm" className="h-8 rounded-full text-slate-500" onClick={() => setShowHelp((v) => !v)} data-testid="calendar-import-help-btn">
                <HelpCircle className="mr-1.5 h-4 w-4" /> {showHelp ? "Hide" : "Where do I find the link?"}
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Paste the <strong>iCal / .ics link</strong> of a single Google or Apple calendar. RallyPointGo pulls its events into
              your team schedule (read-only) and auto-refreshes them. Only the one calendar you link is imported.
              The first import is silent — after that, newly added events notify your team.
            </p>

            {showHelp && (
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="calendar-import-help">
                <div>
                  <p className="mb-1 font-semibold text-slate-800">🟦 Google Calendar (on a computer)</p>
                  <ol className="ml-4 list-decimal space-y-0.5">
                    <li>Open <strong>calendar.google.com</strong> and sign in.</li>
                    <li>Hover the calendar under “My calendars” → click <strong>⋮</strong> → <strong>Settings and sharing</strong>.</li>
                    <li>Scroll to <strong>“Integrate calendar.”</strong></li>
                    <li>Copy the <strong>“Secret address in iCal format”</strong> (private) or <strong>“Public address in iCal format.”</strong></li>
                    <li>The link ends in <strong>/basic.ics</strong> — paste it below.</li>
                  </ol>
                </div>
                <div>
                  <p className="mb-1 font-semibold text-slate-800">🍎 Apple / iCloud Calendar (Mac or iCloud.com)</p>
                  <ol className="ml-4 list-decimal space-y-0.5">
                    <li>Open <strong>Calendar</strong> (Mac) or <strong>iCloud.com → Calendar</strong>.</li>
                    <li>Click the <strong>share icon</strong> next to the calendar name.</li>
                    <li>Turn on <strong>“Public Calendar.”</strong></li>
                    <li>Click <strong>“Share Link” / Copy Link</strong> (starts with <strong>webcal://</strong>).</li>
                    <li>Paste it below — RallyPointGo accepts <strong>webcal://</strong> links.</li>
                  </ol>
                </div>
                <p className="rounded-lg bg-amber-50 p-2 text-amber-700">
                  ⚠️ Don’t paste RallyPointGo’s own “Copy calendar link” here — that would loop its events back in. These links contain a
                  secret token, so treat them like a password.
                </p>
              </div>
            )}

            {imports.length > 0 && (
              <div className="space-y-2" data-testid="calendar-import-list">
                {imports.map((imp) => (
                  <div key={imp.id} className="rounded-xl border border-slate-200 p-3" data-testid="calendar-import-item">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">{imp.name}</p>
                        <p className="flex items-center gap-1 truncate text-xs text-slate-400"><Link2 className="h-3 w-3 shrink-0" /> {hostOf(imp.url)}</p>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={busyId === imp.id} onClick={() => syncNow(imp)} data-testid={`calendar-import-sync-${imp.id}`} title="Sync now">
                          {busyId === imp.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setDel(imp)} data-testid={`calendar-import-remove-${imp.id}`} title="Disconnect">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{imp.event_count} events</span>
                      <span className="text-slate-400">{fmtSynced(imp.last_synced)}</span>
                      {imp.last_status === "error" && (
                        <span className="inline-flex items-center gap-1 text-red-500" title={imp.last_error || ""}><AlertCircle className="h-3.5 w-3.5" /> Sync error</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3 rounded-xl border border-dashed border-slate-300 p-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Calendar name <span className="text-slate-400">(optional)</span></Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Coach Google calendar" data-testid="calendar-import-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">iCal / .ics link</Label>
                <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://calendar.google.com/.../basic.ics" data-testid="calendar-import-url-input" />
              </div>
              <Button onClick={connect} disabled={connecting} className="w-full rounded-full bg-slate-900 hover:bg-slate-800" data-testid="calendar-import-connect-btn">
                {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Connect calendar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!del} onOpenChange={(o) => !o && setDel(null)}
        title="Disconnect this calendar?"
        description={del ? `This will remove all ${del.event_count} event(s) imported from "${del.name}". Your own RallyPointGo events are not affected.` : ""}
        confirmText="Disconnect" destructive onConfirm={doDisconnect} testId="calendar-import-confirm-remove"
      />
    </>
  );
}
